class StickyAddToCart extends HTMLElement {
  constructor() {
    super();

    this.variants = [];
    this.currentVariant = null;
    this.productForm = null;
    this.mainSubmitButton = null;
    this.variantInput = null;
    this.variantText = null;
    this.stickyButton = null;

    this.buttonText = null;
    this.isAdding = false;
    this.loadingTimeout = null;

    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    this.variantText = this.querySelector('[data-sticky-atc-variant]');
    this.stickyButton = this.querySelector('[data-sticky-atc-button]');
    this.buttonText = this.querySelector('[data-sticky-atc-button-text]');

    this.variants = this.getVariants();
    this.productForm = this.getProductForm();
    this.mainSubmitButton = this.getMainSubmitButton();
    this.variantInput = this.getVariantInput();

    this.updateFromCurrentVariant();
    this.bindEvents();
    this.setupVisibilityEvents();
    this.updateVisibility();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
  }

  getVariants() {
    const script = this.querySelector('[data-sticky-atc-variants]');

    if (!script) return [];

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('Sticky add to cart: could not parse variants.', error);
      return [];
    }
  }

  getProductForm() {
    return document.querySelector('form[action*="/cart/add"]');
  }

  getMainSubmitButton() {
    return (
      document.querySelector('[id^="ProductSubmitButton-"]:not([data-sticky-atc-button])') ||
      document.querySelector('button[name="add"]:not([data-sticky-atc-button])') ||
      document.querySelector('form[action*="/cart/add"] [type="submit"]:not([data-sticky-atc-button])')
    );
  }

  getVariantInput() {
    if (!this.productForm) {
      this.productForm = this.getProductForm();
    }

    if (!this.productForm) return null;

    return this.productForm.querySelector('input[name="id"]');
  }

  bindEvents() {
    if (this.stickyButton) {
      this.stickyButton.addEventListener('click', () => {
        this.handleStickyButtonClick();
      });
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.setLoading(false);
        window.clearTimeout(this.loadingTimeout);
      });

      subscribe(PUB_SUB_EVENTS.cartError, () => {
        this.setLoading(false);
        window.clearTimeout(this.loadingTimeout);
      });
    }

    document.addEventListener('change', () => {
      window.setTimeout(() => {
        this.updateFromCurrentVariant();
      }, 0);
    });

    document.addEventListener('variant:change', (event) => {
      if (event.detail && event.detail.variant) {
        this.updateVariant(event.detail.variant);
      } else {
        this.updateFromCurrentVariant();
      }
    });

    document.addEventListener('variant:changed', (event) => {
      if (event.detail && event.detail.variant) {
        this.updateVariant(event.detail.variant);
      } else {
        this.updateFromCurrentVariant();
      }
    });
  }

  setupVisibilityEvents() {
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize);
  }

  handleScroll() {
    window.requestAnimationFrame(() => {
      this.updateVisibility();
    });
  }

  handleResize() {
    this.updateVisibility();
  }

  updateVisibility() {
    this.mainSubmitButton = this.getMainSubmitButton();

    if (!this.mainSubmitButton) {
      return;
    }

    const rect = this.mainSubmitButton.getBoundingClientRect();

    const mainButtonIsAboveViewport = rect.bottom < 0;

    if (mainButtonIsAboveViewport) {
      this.classList.add('is-visible');
    } else {
      this.classList.remove('is-visible');
    }
  }

  updateFromCurrentVariant() {
    this.variantInput = this.getVariantInput();

    if (!this.variantInput) return;

    const variantId = Number(this.variantInput.value);
    const variant = this.variants.find((item) => Number(item.id) === variantId);

    if (variant) {
      this.updateVariant(variant);
    }
  }

  updateVariant(variant) {
    this.currentVariant = variant;

    if (this.variantText) {
      this.variantText.textContent = this.getVariantLabel(variant);
    }

    if (this.stickyButton && this.buttonText) {
      if (variant.available) {
        this.stickyButton.disabled = this.isAdding;
        this.buttonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
      } else {
        this.stickyButton.disabled = true;
        this.buttonText.textContent = window.variantStrings?.soldOut || 'Sold out';
      }
    }
  }

  getVariantLabel(variant) {
    const price = variant.formatted_price || this.formatMoney(variant.price);

    if (!variant.title || variant.title === 'Default Title') {
      return price;
    }

    const displayValue = variant.display_value || variant.title;

    return `${displayValue} · ${price}`;
  }

  formatMoney(cents) {
    const currency = this.dataset.currency || 'EUR';
    const locale = this.dataset.locale || document.documentElement.lang || 'en';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      }).format(cents / 100);
    } catch (error) {
      return `${(cents / 100).toFixed(2)} ${currency}`;
    }
  }

  handleStickyButtonClick() {
    if (this.isAdding) return;

    this.mainSubmitButton = this.getMainSubmitButton();

    if (!this.mainSubmitButton || this.mainSubmitButton.disabled) {
      return;
    }

    this.trackStickyAtcClick();
    this.setLoading(true);

    this.mainSubmitButton.click();

    window.clearTimeout(this.loadingTimeout);

    this.loadingTimeout = window.setTimeout(() => {
      this.setLoading(false);
    }, 5000);
  }

  setLoading(isLoading) {
    this.isAdding = isLoading;

    if (!this.stickyButton) return;

    this.stickyButton.classList.toggle('is-loading', isLoading);
    this.stickyButton.disabled = isLoading || !this.currentVariant?.available;
    this.stickyButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  trackStickyAtcClick() {
    window.dataLayer = window.dataLayer || [];

    const productTitle = this.stickyButton?.dataset.productTitle || '';
    const productHandle = this.stickyButton?.dataset.productHandle || '';
    const variantId = this.currentVariant?.id || this.variantInput?.value || '';
    const variantTitle = this.currentVariant?.display_value || this.currentVariant?.title || '';
    const price = this.currentVariant?.price ? this.currentVariant.price / 100 : null;
    const currency = this.dataset.currency || '';

    window.dataLayer.push({
      event: 'pdp_sticky_atc_click',
      ecommerce: {
        currency: currency,
        value: price,
        items: [
          {
            item_id: String(variantId),
            item_name: productTitle,
            item_variant: variantTitle,
            price: price,
            quantity: 1,
          },
        ],
      },
      product_handle: productHandle,
      product_variant: variantTitle,
    });
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
